import React, { useState, useRef, useEffect, useCallback } from "react";
import { Globe, ChevronDown } from "lucide-react";

const languages = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "zh-CN", label: "中文", flag: "🇨🇳" },
  { code: "ms", label: "Bahasa Melayu", flag: "🇲🇾" },
  { code: "ta", label: "தமிழ்", flag: "🇮🇳" }
];

// Comprehensive cookie clearing function
const clearAllGoogleTranslateCookies = () => {
  const hostname = window.location.hostname;
  const hostParts = hostname.split('.');
  
  // Generate all possible domain variations
  const domains = ['', hostname];
  for (let i = 0; i < hostParts.length; i++) {
    domains.push('.' + hostParts.slice(i).join('.'));
  }
  
  const paths = ['/', ''];
  
  // Clear googtrans cookie on all domain/path combinations
  domains.forEach(domain => {
    paths.forEach(path => {
      const domainPart = domain ? `; domain=${domain}` : '';
      const pathPart = `; path=${path || '/'}`;
      
      // Try multiple expiry formats
      document.cookie = `googtrans=${pathPart}${domainPart}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      document.cookie = `googtrans=${pathPart}${domainPart}; max-age=0`;
    });
  });
  
  // Also try clearing without any domain specification
  document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  document.cookie = 'googtrans=; max-age=0; path=/';
};

// Function to restore original page
const restoreOriginalPage = () => {
  // Clear all cookies first
  clearAllGoogleTranslateCookies();
  
  // Remove Google Translate iframe and elements
  const elementsToRemove = document.querySelectorAll(
    '.goog-te-banner-frame, .goog-te-menu-frame, .goog-te-menu-value, ' +
    '#goog-gt-tt, .goog-te-spinner-pos, .goog-te-balloon-frame, ' +
    'iframe.goog-te-menu-frame, .skiptranslate'
  );
  elementsToRemove.forEach(el => {
    try { el.remove(); } catch (e) { /* ignore */ }
  });
  
  // Reset body positioning that Google Translate might have changed
  document.body.style.top = '';
  document.body.style.position = '';
  
  // Remove the google translate script to prevent re-initialization
  const scripts = document.querySelectorAll('script[src*="translate.google"]');
  scripts.forEach(script => {
    try { script.remove(); } catch (e) { /* ignore */ }
  });
  
  // Clear Google Translate global objects
  if (window.google && window.google.translate) {
    try {
      delete window.google.translate;
    } catch (e) { /* ignore */ }
  }
  if (window.googleTranslateElementInit) {
    try {
      delete window.googleTranslateElementInit;
    } catch (e) { /* ignore */ }
  }
  
  // Force reload with cache bypass
  window.location.href = window.location.pathname + '?_=' + Date.now();
};

const LanguageSelector = ({ variant = "light" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Get current language from cookie
  const getInitialLang = () => {
    if (typeof window !== "undefined") {
      // Check for our custom lang parameter (used after English reset)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('_')) {
        // This is a reset request, return English
        return "en";
      }
      
      // Check Google Translate cookie
      const cookies = document.cookie.split("; ");
      for (const cookie of cookies) {
        if (cookie.startsWith("googtrans=")) {
          const value = cookie.substring("googtrans=".length);
          const langMatch = value.match(/\/en\/([a-zA-Z-]+)/);
          if (langMatch && langMatch[1]) {
            return langMatch[1];
          }
        }
      }
    }
    return "en";
  };
  
  const [currentLang, setCurrentLang] = useState(getInitialLang);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize Google Translate
  useEffect(() => {
    // Only load Google Translate if we need it (non-English language)
    const initLang = getInitialLang();
    
    if (initLang !== "en") {
      // Initialize Google Translate
      if (!window.googleTranslateElementInit) {
        window.googleTranslateElementInit = () => {
          if (window.google && window.google.translate) {
            new window.google.translate.TranslateElement(
              {
                pageLanguage: "en",
                includedLanguages: "en,zh-CN,ms,ta",
                autoDisplay: false,
                layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE
              },
              "google_translate_element"
            );
          }
        };

        // Load Google Translate script
        const existingScript = document.querySelector('script[src*="translate.google"]');
        if (!existingScript) {
          const script = document.createElement("script");
          script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
          script.async = true;
          document.body.appendChild(script);
        }
      }
    }
  }, []);

  const handleLanguageChange = useCallback((langCode) => {
    setCurrentLang(langCode);
    setIsOpen(false);

    if (langCode === "en") {
      // Restore to English - comprehensive reset
      restoreOriginalPage();
    } else {
      // Set translation cookie for the selected language
      const hostname = window.location.hostname;
      const hostParts = hostname.split('.');
      const rootDomain = hostParts.length > 1 
        ? '.' + hostParts.slice(-2).join('.') 
        : hostname;
      
      // Set cookies on multiple domains to ensure it works
      document.cookie = `googtrans=/en/${langCode}; path=/`;
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${hostname}`;
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=${rootDomain}`;
      
      // Try to trigger Google Translate via the hidden select
      const googleCombo = document.querySelector(".goog-te-combo");
      if (googleCombo) {
        googleCombo.value = langCode;
        googleCombo.dispatchEvent(new Event("change"));
      } else {
        // Reload to apply translation
        window.location.reload();
      }
    }
  }, []);

  const currentLanguage = languages.find(l => l.code === currentLang) || languages[0];
  
  // Styling based on variant
  const isDark = variant === "dark";

  return (
    <div className="relative notranslate" translate="no" ref={dropdownRef}>
      {/* Hidden Google Translate Element */}
      <div id="google_translate_element" style={{ display: 'none' }}></div>
      
      {/* Custom Language Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded border-2 transition-all ${
          isDark 
            ? "border-white/50 bg-white/10 hover:bg-white/20 text-white" 
            : "border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
        }`}
        title="Select Language"
        type="button"
      >
        <Globe size={16} />
        <span className="text-sm font-bold hidden sm:inline">{currentLanguage.flag}</span>
        <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute right-0 top-full mt-1 w-44 rounded border-2 border-black shadow-lg z-50 ${
          isDark ? "bg-gray-800" : "bg-white"
        }`}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
                currentLang === lang.code 
                  ? isDark 
                    ? "bg-white/20 text-white" 
                    : "bg-primary/10 text-primary"
                  : isDark
                    ? "text-white hover:bg-white/10"
                    : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <span className="text-lg">{lang.flag}</span>
              <span>{lang.label}</span>
              {currentLang === lang.code && (
                <span className="ml-auto text-xs">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
